import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';

import api from '../../api';

export const getAllEvents = createAsyncThunk(
  'events/getAllEvents',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get('/events');
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to fetch all events.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const getSingleEvent = createAsyncThunk(
  'events/getSingleEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/events/${eventId}`);
      return res.data.data.event;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to get that event.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteEvent = createAsyncThunk(
  'events/deleteEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      await api.delete(`/events/${eventId}`);
      return eventId;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to delete that event.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const addEvent = createAsyncThunk(
  'events/addEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const res = await api.post('/events', eventData);
      return res.data.data;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to add that event.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateEvent = createAsyncThunk(
  'events/updateEvent',
  async (eventData, { rejectWithValue }) => {
    try {
      const response = await api.put(`/events/${eventData.id}`, eventData);
      if (!response) {
        return Error('Failed to update event');
      }
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  events: [],
  upcomingEvents: [],
  pastEvents: [],
  loading: false,
  selectedEvent: null,
  error: null,
};

const eventsSlice = createSlice({
  name: 'events',
  initialState,

  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.events = action.payload;

        // Automatically separate events by date (in PST timezone)
        // Get today's date in PST as YYYY-MM-DD string
        const nowPST = new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' });
        const todayPST = new Date(nowPST);
        const todayYear = todayPST.getFullYear();
        const todayMonth = todayPST.getMonth();
        const todayDay = todayPST.getDate();
        const todayDateOnly = new Date(todayYear, todayMonth, todayDay);

        state.upcomingEvents = state.events.filter((event) => {
          // Use startDate (new field) or date (old field) for backwards compatibility
          const dateField = event.startDate || event.date;
          if (!dateField) return false; // Skip events without dates

          const dateStr = dateField.split('T')[0];
          const [year, month, day] = dateStr.split('-');
          const eventDateOnly = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return eventDateOnly >= todayDateOnly;
        });

        state.pastEvents = state.events.filter((event) => {
          // Use startDate (new field) or date (old field) for backwards compatibility
          const dateField = event.startDate || event.date;
          if (!dateField) return false; // Skip events without dates

          const dateStr = dateField.split('T')[0];
          const [year, month, day] = dateStr.split('-');
          const eventDateOnly = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return eventDateOnly < todayDateOnly;
        });
      })
      .addCase(getAllEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getSingleEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSingleEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedEvent = action.payload;
      })
      .addCase(getSingleEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.events = state.events.filter((item) => item._id !== action.payload);
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(addEvent.fulfilled, (state, action) => {
        state.events.push(action.payload);
        state.loading = false;
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      .addCase(addEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.events.findIndex((item) => item._id === action.payload.event._id);
        if (index !== -1) {
          state.events[index] = action.payload.event;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = eventsSlice.actions;

export default eventsSlice.reducer;
